import { useState, useEffect, useCallback } from "react";
import useWindowSize from "../hooks/useWindowSize";
import apiService from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";
import ConfirmationModal from "../components/ConfirmationModal";
import NotificationModal from "../components/NotificationModal";
import { invalidateDashboardCache } from "./HomePage"; // Import cache invalidation function
import "../styles/pages/ResidentDetailsPage.css";



/* ═══════════════════════ RESIDENT DETAILS PAGE ═══════════════════════ */
export default function ResidentDetailsPage({ residentId, onBack, onNavigateToScreen, backButtonText = "Back to Seniors" }) {
  const { isMobile } = useWindowSize();
  
  const [resident, setResident] = useState(null);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [highlights, setHighlights] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [caregiverToDelete, setCaregiverToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Notification modal state
  const [notification, setNotification] = useState({ show: false, type: 'info', message: '' });

    const handleDeleteCaregiver = async () => {
      if (!caregiverToDelete) return;
    
      try {
        setDeleteLoading(true);
        await apiService.removeCaregiverFromResident(residentId, caregiverToDelete.caregiver_id);
      
        // Invalidate HomePage cache so it refreshes on next visit
        invalidateDashboardCache();
      
        // Refresh caregivers list
        const caregiversData = await apiService.getResidentCaregivers(residentId, 7);
        setCaregivers(caregiversData?.caregivers || []);
      
        // Close modal and reset state
        setShowDeleteModal(false);
        setCaregiverToDelete(null);
      
        // Show success notification
        setNotification({
          show: true,
          type: 'success',
          message: 'Caregiver removed successfully!'
        });
      } catch (err) {
        console.error('Failed to remove caregiver:', err);
      
        // Show error notification
        setNotification({
          show: true,
          type: 'error',
          message: `Failed to remove caregiver: ${err.message || 'Unknown error'}`
        });
      } finally {
        setDeleteLoading(false);
      }
    };

    const loadResidentDetails = useCallback(async () => {
        // Validate residentId before making API calls
        if (!residentId) {
          console.error('[ResidentDetailsPage] No residentId provided');
          setError(new Error('No resident ID provided'));
          setLoading(false);
          return;
        }

        console.log('[ResidentDetailsPage] Loading details for resident:', residentId);

        try {
          setLoading(true);
          setError(null);
      
      // Add minimum loading time to prevent flash of loading state
      const minLoadTime = new Promise(resolve => setTimeout(resolve, 300));
      
      // Fetch resident details (this is the critical one)
      const residentDataPromise = apiService.getResidentDetails(residentId);
      
      // Wait for both minimum time and data fetch
      const [residentData] = await Promise.all([residentDataPromise, minLoadTime]);
      setResident(residentData);
      
      // Fetch emergency contacts - don't let this fail the whole page
      try {
        const contacts = await apiService.getEmergencyContacts(residentId);
        setEmergencyContacts(contacts || []);
      } catch (contactErr) {
        console.warn('Could not load emergency contacts:', contactErr);
        setEmergencyContacts([]);
      }
      
      // Fetch assigned caregivers - don't let this fail the whole page
      try {
        const caregiversData = await apiService.getResidentCaregivers(residentId, 7);
        setCaregivers(caregiversData?.caregivers || []);
      } catch (cgErr) {
        console.warn('Could not load caregivers:', cgErr);
        setCaregivers([]);
      }
      
      // Fetch highlights - don't let this fail the whole page
      try {
        const highlightsData = await apiService.getResidentHighlights(residentId);
        setHighlights(highlightsData || {});
      } catch (highlightErr) {
        console.warn('Could not load highlights:', highlightErr);
        setHighlights({});
      }
      
    } catch (err) {
      console.error('Failed to load resident details:', err);
      setError(err);
    } finally {
            setLoading(false);
    }
  }, [residentId]);

  useEffect(() => {
    loadResidentDetails();
  }, [loadResidentDetails]);

    if (loading) {
    return (
      <main className="resident-details-loading-container">
        <LoadingSpinner message="Loading resident details..." />
      </main>
    );
  }

      if (error) {
    return (
      <main className="resident-details-loading-container">
        <ErrorMessage 
          error={error} 
          onRetry={loadResidentDetails} 
          onBack={onBack}
          residentId={residentId}
        />
      </main>
    );
  }

  

    return (
    <main style={{
      flex: 1,
      padding: isMobile ? "8px" : "16px 60px",
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      minWidth: 0,
      fontFamily: "'Segoe UI', sans-serif",
      color: "#ffffff",
      background: "linear-gradient(160deg, #b8d4f0 0%, #9ec4ea 30%, #7daed8 60%, #6a9ccc 100%)",
      paddingTop: isMobile ? 75 : 82,
      paddingBottom: 20,
      minHeight: "100vh",
      boxSizing: "border-box",
    }}>
      
            {/* ═══ BACK BUTTON & HEADER ═══ */}
      <div className="resident-details-header">
        <button onClick={onBack} className="resident-details-back-button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          {backButtonText}
        </button>
        
        <h1 className={`resident-details-title ${isMobile ? "mobile" : ""}`}>
          Resident Details
        </h1>
      </div>

            {/* ═══ PROFILE SECTION (Photo + Details) ═══ */}
      <div className={`resident-details-profile ${isMobile ? "mobile" : ""}`}>
        {/* Profile Photo */}
        <div className="resident-details-photo-container" onContextMenu={(e) => e.preventDefault()}>
          <div 
            className={`resident-details-photo ${isMobile ? "mobile" : ""}`}
            onContextMenu={(e) => e.preventDefault()}
          >
            {resident?.profile_photo ? (
              <img 
                src={resident.profile_photo} 
                alt={resident.name}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentNode.querySelector('svg') && (e.target.parentNode.querySelector('svg').style.display = 'block');
                }}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                draggable={false}
              />
            ) : (
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="8" r="5"/>
                <path d="M3 21v-2a4 4 0 014-4h10a4 4 0 014 4v2"/>
              </svg>
            )}
          </div>
          <div className={`resident-details-status-badge resident-details-status-badge--${(resident?.status || 'ACTIVE').toLowerCase()}`}>
            {resident?.status || "ACTIVE"}
          </div>
        </div>

        {/* Resident Details */}
        <div className={`resident-details-grid ${isMobile ? "mobile" : ""}`}>
          <DetailField label="Name" value={resident?.name || "N/A"} />
          <DetailField label="MRN" value={resident?.mrn || "N/A"} />
          <DetailField label="Room" value={resident?.room_id || "N/A"} />
          <DetailField label="Age" value={`${resident?.age || "N/A"} years`} />
          <DetailField label="Gender" value={resident?.gender || "N/A"} />
          <DetailField label="Admission Date" value={formatDate(resident?.admission_date)} />
          <DetailField label="Fall Risk Level" value={resident?.fall_risk_level || "MODERATE"} valueColor={getRiskColor(resident?.fall_risk_level)} />
          <DetailField label="Sleep Quality" value={resident?.latest_sleep_quality || "AVERAGE"} valueColor={getQualityColor(resident?.latest_sleep_quality)} />
        </div>
      </div>

            {/* ═══ EMERGENCY CONTACTS SECTION ═══ */}
      <div className={`resident-details-section ${isMobile ? "mobile" : ""}`}>
        <h2 className={`resident-details-section__title ${isMobile ? "mobile" : ""}`}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
          </svg>
          Emergency Contacts
        </h2>
        
        {emergencyContacts.length === 0 ? (
          <p className="resident-details-section__empty">No emergency contacts available</p>
        ) : (
          <div className={`resident-details-grid-auto ${isMobile ? "mobile" : ""}`}>
            {emergencyContacts.map((contact, idx) => (
              <EmergencyContactCard key={idx} contact={contact} isMobile={isMobile} />
            ))}
          </div>
        )}
      </div>

            {/* ═══ ASSIGNED CAREGIVERS SECTION ═══ */}
      <div className={`resident-details-section ${isMobile ? "mobile" : ""}`}>
        <h2 className={`resident-details-section__title ${isMobile ? "mobile" : ""}`}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
          </svg>
          Current Caregivers
        </h2>
        
        {caregivers.length === 0 ? (
          <p className="resident-details-section__empty">No caregivers currently assigned</p>
        ) : (
          <div className={`resident-details-grid-auto ${isMobile ? "mobile" : ""}`}>
            {caregivers.map((caregiver, idx) => (
              <CaregiverAssignmentCard 
                key={idx} 
                caregiver={caregiver} 
                isMobile={isMobile}
                onNavigateToCaregiver={(caregiverId) => {
                  onNavigateToScreen && onNavigateToScreen('Caregivers', caregiverId);
                }}
                onDelete={(cg) => {
                  setCaregiverToDelete(cg);
                  setShowDeleteModal(true);
                }}
              />
            ))}
          </div>
        )}
      </div>

            {/* ═══ HIGHLIGHTS SECTION ═══ */}
      <div className={`resident-details-section ${isMobile ? "mobile" : ""}`}>
        <h2 className={`resident-details-section__title ${isMobile ? "mobile" : ""}`}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
          </svg>
          Recent Activity Highlights
        </h2>
        
        <div className={`resident-details-grid-2 ${isMobile ? "mobile" : ""}`}>
          <HighlightCard 
            title="Last Sleep Session"
            value={highlights?.last_sleep_hours ? `${highlights.last_sleep_hours} hrs` : "N/A"}
            subtitle={highlights?.last_sleep_date ? formatDate(highlights.last_sleep_date) : ""}
            icon="sleep"
            onClick={() => onNavigateToScreen && onNavigateToScreen('Sleep Diary', residentId)}
          />
          <HighlightCard 
            title="Recent Falls"
            value={highlights?.recent_falls_count != null ? String(highlights.recent_falls_count) : "N/A"}
            subtitle="Last 30 days"
            icon="fall"
            onClick={() => onNavigateToScreen && onNavigateToScreen('Falls', residentId)}
          />
          <HighlightCard 
            title="Gait Score"
            value={highlights?.gait_score ? `${highlights.gait_score}%` : "N/A"}
            subtitle="Balance & Mobility"
            icon="gait"
            onClick={() => onNavigateToScreen && onNavigateToScreen('Gait', residentId)}
          />
          <HighlightCard 
            title="ADL Completion"
            value={highlights?.adl_completion ? `${highlights.adl_completion}%` : "N/A"}
            subtitle="Daily activities"
            icon="adl"
            onClick={() => onNavigateToScreen && onNavigateToScreen('ADLs', residentId)}
          />
        </div>
      </div>

            {/* ═══ DELETE CONFIRMATION MODAL ═══ */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onConfirm={handleDeleteCaregiver}
        onCancel={() => {
          setShowDeleteModal(false);
          setCaregiverToDelete(null);
        }}
        title="Remove Caregiver"
        message={`Are you sure you want to remove ${caregiverToDelete?.display_name?.split(",")[0] || 'this caregiver'} from this resident?`}
        confirmText="Remove Caregiver"
        cancelText="Cancel"
        variant="warning"
        loading={deleteLoading}
        isMobile={isMobile}
        details={
          caregiverToDelete && (
            <>
              <div><strong>Role:</strong> {caregiverToDelete.role || "N/A"}</div>
              <div><strong>Shift:</strong> {caregiverToDelete.shift_type || caregiverToDelete.primary_shift || "N/A"}</div>
              <div><strong>Assignment Type:</strong> {caregiverToDelete.assignment_type || "PRIMARY"}</div>
            </>
          )
        }
      />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        type={notification.type}
        message={notification.message}
        autoDismiss={3}
        isMobile={isMobile}
      />
    </main>
  );
}

/* ═══════════════════════ DETAIL FIELD COMPONENT ═══════════════════════ */
function DetailField({ label, value, valueColor }) {
  return (
    <div className="resident-details-field">
      <span className="resident-details-field__label">
        {label}
      </span>
      <span className="resident-details-field__value" style={{ color: valueColor || "#ffffff" }}>
        {value}
      </span>
    </div>
  );
}

/* ═══════════════════════ EMERGENCY CONTACT CARD ═══════════════════════ */
function EmergencyContactCard({ contact, isMobile }) {
  const priorityClass = `emergency-contact-card__priority--${(contact.contact_priority || 'tertiary').toLowerCase()}`;
  
  return (
    <div className={`emergency-contact-card ${isMobile ? "mobile" : ""}`}>
      <div className="emergency-contact-card__header">
        <span className="emergency-contact-card__name">
          {contact.contact_name || contact.contact_name_enc || "Unknown"}
        </span>
        <span className={`emergency-contact-card__priority ${priorityClass}`}>
          {contact.contact_priority || "N/A"}
        </span>
      </div>
      
      <div className="emergency-contact-card__details">
        <div className="emergency-contact-card__detail">
          <strong>Relationship:</strong> {contact.relationship || "N/A"}
        </div>
        <div className="emergency-contact-card__detail">
          <strong>Phone:</strong> {contact.phone || contact.phone_enc || "N/A"}
        </div>
        {(contact.email || contact.email_enc) && (
          <div className="emergency-contact-card__detail">
            <strong>Email:</strong> {contact.email || contact.email_enc}
          </div>
        )}
      </div>
      
      {(contact.notify_on_fall || contact.is_legal_guardian) && (
        <div className="emergency-contact-card__badges">
          {contact.notify_on_fall && (
            <span className="emergency-contact-card__badge emergency-contact-card__badge--fall">
              🚨 Fall Notifications
            </span>
          )}
          {contact.is_legal_guardian && (
            <span className="emergency-contact-card__badge emergency-contact-card__badge--guardian">
              👤 Legal Guardian
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════ HIGHLIGHT CARD ═══════════════════════ */
function HighlightCard({ title, value, subtitle, icon, onClick }) {
  const getIcon = () => {
    switch(icon) {
      case "sleep":
        return <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>;
      case "fall":
        return <path d="M12 2v20M17 7l-5-5-5 5M17 17l-5 5-5-5"/>;
      case "gait":
        return <><circle cx="12" cy="8" r="3"/><path d="M12 11v9m0-9l-3 3m3-3l3 3"/></>;
      case "adl":
        return <><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></>;
      default:
        return <circle cx="12" cy="12" r="10"/>;
    }
  };

  return (
    <button onClick={onClick} className="highlight-card">
      <div className="highlight-card__icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          {getIcon()}
        </svg>
      </div>
      
      <div className="highlight-card__content">
        <div className="highlight-card__title">
          {title}
        </div>
        <div className="highlight-card__value">
          {value}
        </div>
        {subtitle && (
          <div className="highlight-card__subtitle">
            {subtitle}
          </div>
        )}
      </div>

      {/* Arrow Icon */}
      {onClick && (
        <div className="highlight-card__arrow">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </div>
      )}
    </button>
  );
}

/* ═══════════════════════ CAREGIVER ASSIGNMENT CARD ═══════════════════════ */
function CaregiverAssignmentCard({ caregiver, isMobile, onNavigateToCaregiver, onDelete }) {
  return (
    <div style={{
      background: "rgba(255,255,255,0.1)",
      border: "1.5px solid rgba(255,255,255,0.3)",
      borderRadius: 12,
      padding: isMobile ? "14px" : "16px",
      display: "flex",
      flexDirection: "column",
      gap: 12,
      transition: "all 0.2s",
      position: "relative",
    }}
    >
            {/* Action Buttons - Top Right Corner */}
            <div style={{
              position: "absolute",
              top: 12,
              right: 12,
              display: "flex",
              gap: 8,
              zIndex: 10,
            }}>
              {/* Delete Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete && onDelete(caregiver);
                }}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "rgba(255,107,107,0.2)",
                  border: "1.5px solid rgba(255,107,107,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,107,107,0.4)";
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,107,107,0.2)";
                  e.currentTarget.style.transform = "scale(1)";
                }}
                title="Remove caregiver assignment"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>

              {/* View Details Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigateToCaregiver && onNavigateToCaregiver(caregiver.caregiver_id);
                }}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "rgba(74,144,226,0.3)",
                  border: "1.5px solid rgba(74,144,226,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(74,144,226,0.5)";
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(74,144,226,0.3)";
                  e.currentTarget.style.transform = "scale(1)";
                }}
                title="View caregiver details"
              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            </div>
      
            {/* Header with Name and Status */}
      <div style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 8,
        paddingRight: 80, // Make room for both action buttons
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flex: 1,
          minWidth: 0,
        }}>
          {/* Profile Icon */}
          <div style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "#D9D9D9",
            border: "2px solid #FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#042558" strokeWidth="2">
              <circle cx="12" cy="8" r="4"/>
              <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/>
            </svg>
          </div>
          
          {/* Name and Badge */}
          <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
              fontSize: 16,
              fontWeight: 700,
              color: "#ffffff",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}>
              {caregiver.display_name?.split(",")[0] || 
                `${caregiver.first_name || ''} ${caregiver.last_name || ''}`.trim() || 
                'Unknown'}
            </div>
            <div style={{
              fontSize: 12,
              color: "rgba(255,255,255,0.7)",
              marginTop: 2,
            }}>
              {caregiver.badge_id || "N/A"}
            </div>
          </div>
        </div>
      </div>
      
      {/* Status Badge - Moved Below Name */}
      <div style={{
        display: "flex",
        justifyContent: "flex-start",
        marginTop: -4,
      }}>
                <span style={{
          fontSize: 10,
          fontWeight: 700,
          color: "#ffffff",
          background: caregiver.status === "ACTIVE" ? "#6ADD00" : "#FFA500",
          padding: "4px 8px",
          borderRadius: 6,
          textTransform: "uppercase",
        }}>
          {caregiver.status || "ACTIVE"}
        </span>
      </div>
      
      {/* Details Grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 10,
        paddingTop: 8,
        borderTop: "1px solid rgba(255,255,255,0.2)",
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{
            fontSize: 11,
            color: "rgba(255,255,255,0.6)",
            fontWeight: 600,
            textTransform: "uppercase",
          }}>
            Role
          </span>
                    <span style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#ffffff",
          }}>
            {caregiver.role || "N/A"}
          </span>
        </div>
        
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{
            fontSize: 11,
            color: "rgba(255,255,255,0.6)",
            fontWeight: 600,
            textTransform: "uppercase",
          }}>
            Shift
          </span>
          <span style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#ffffff",
          }}>
            {caregiver.shift_type || caregiver.primary_shift || "N/A"}
          </span>
        </div>
      </div>

      {/* Assignment Info */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingTop: 8,
        borderTop: "1px solid rgba(255,255,255,0.2)",
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6ADD00" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
            <path d="M22 4L12 14.01l-3-3"/>
          </svg>
          <span style={{
            fontSize: 12,
            color: "rgba(255,255,255,0.8)",
            fontWeight: 600,
          }}>
            {caregiver.assignment_type || "PRIMARY"}
          </span>
        </div>
        
        {caregiver.assigned_date && caregiver.assigned_date !== 'N/A' && (
          <span style={{
            fontSize: 11,
            color: "rgba(255,255,255,0.6)",
          }}>
            Assigned: {formatDate(caregiver.assigned_date)}
          </span>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════ HELPER FUNCTIONS ═══════════════════════ */
function formatDate(dateString) {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
}

function getRiskColor(level) {
  switch(level?.toUpperCase()) {
    case "HIGH": return "#FF6B6B";
    case "MODERATE": return "#FFA500";
    case "LOW": return "#6ADD00";
    default: return "#4A90E2";
  }
}

function getQualityColor(quality) {
  switch(quality?.toUpperCase()) {
    case "GOOD": return "#6ADD00";
    case "AVERAGE": return "#FFA500";
    case "POOR": return "#FF6B6B";
    default: return "#4A90E2";
  }
}
